/**
 * DM Chat Hooks
 * React Query hooks for direct messaging conversations and messages
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  createExpertChat,
  createOrGetConversation,
  deleteConversation,
  deleteMessage,
  editMessage,
  getConversation,
  listConversations,
  markAsRead,
  sendMessage,
} from "../services/dm-chat.service"

export function useDmConversations(page = 1, limit = 20) {
  return useQuery({
    queryKey: ["dm-conversations", page, limit],
    queryFn: () => listConversations(page, limit),
    staleTime: 30 * 1000, // 30 seconds
  })
}

export function useDmConversation(id: string | undefined) {
  return useQuery({
    queryKey: ["dm-conversation", id],
    queryFn: () => getConversation(id as string),
    enabled: !!id,
    staleTime: 10 * 1000,
  })
}

export function useCreateDmConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (targetUserId: string) => createOrGetConversation(targetUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dm-conversations"] })
      queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
    },
  })
}

/**
 * Create or get the booked-session "Chat with Expert" DM. Backend looks up
 * the expert from the user's existing SessionBooking. Pass either ritualId
 * or bookingId.
 */
export function useCreateExpertChat() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (opts: { ritualId?: string; bookingId?: string }) => createExpertChat(opts),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dm-conversations"] })
      queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
    },
  })
}

export function useDeleteDmConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteConversation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dm-conversations"] })
      queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
    },
  })
}

export function useSendDmMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ conversationId, content }: { conversationId: string; content: string }) =>
      sendMessage(conversationId, content),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["dm-conversation", variables.conversationId],
      })
      queryClient.invalidateQueries({ queryKey: ["dm-conversations"] })
      queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
    },
  })
}

export function useEditDmMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      conversationId,
      messageId,
      content,
    }: {
      conversationId: string
      messageId: string
      content: string
    }) => editMessage(conversationId, messageId, content),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["dm-conversation", variables.conversationId],
      })
    },
  })
}

export function useDeleteDmMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ conversationId, messageId }: { conversationId: string; messageId: string }) =>
      deleteMessage(conversationId, messageId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["dm-conversation", variables.conversationId],
      })
      queryClient.invalidateQueries({ queryKey: ["dm-conversations"] })
      queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
    },
  })
}

export function useMarkDmAsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (conversationId: string) => markAsRead(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dm-conversations"] })
      queryClient.invalidateQueries({ queryKey: ["all-conversations"] })
    },
  })
}
